import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

/**
 * D1: Three-tier Agency Hierarchy View
 * Collapsible tree: Holding → Agency → Client
 * Click to enter sub-account context.
 */

interface OrgNode {
  org: {
    id: string;
    name: string;
    slug: string;
    type: string;
    status: string;
    depth: number;
    industry: string | null;
  };
  children: OrgNode[];
  tenants: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
  }>;
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  suspended: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  archived: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
};

const DEPTH_LABELS = ["Holding Company", "Agency", "Client"];

export default function AgencyHierarchyPage() {
  const [tree, setTree] = useState<OrgNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setRootOrgId] = useState<string | null>(null);

  const fetchHierarchy = useCallback(async (orgId: string) => {
    try {
      const data = await apiFetch<{ data: OrgNode }>(`/platform/agency/hierarchy/${orgId}`);
      setTree(data.data);
    } catch {
      // handled by empty state
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // In production: get org from user context
    // For now: fetch from URL param or first org
    const params = new URLSearchParams(window.location.search);
    const orgId = params.get("orgId");
    if (orgId) {
      setRootOrgId(orgId);
      fetchHierarchy(orgId);
    } else {
      setIsLoading(false);
    }
  }, [fetchHierarchy]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-12" />)}
      </div>
    );
  }

  if (!tree) {
    return (
      <div>
        <h1 className="mb-6 text-2xl font-bold">Agency Hierarchy</h1>
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          Select an organization to view its hierarchy.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Agency Hierarchy</h1>
        <span className="text-sm text-muted-foreground">
          {countNodes(tree)} organizations, {countTenants(tree)} accounts
        </span>
      </div>
      <TreeNodeView node={tree} depth={0} />
    </div>
  );
}

function TreeNodeView({ node, depth }: { node: OrgNode; depth: number }) {
  const [isExpanded, setIsExpanded] = useState(depth < 2);
  const hasChildren = node.children.length > 0 || node.tenants.length > 0;
  const depthLabel = DEPTH_LABELS[depth] ?? `Level ${depth}`;

  return (
    <div className={depth > 0 ? "ml-6 border-l border-border pl-4" : ""}>
      {/* Org header */}
      <div
        className="group flex cursor-pointer items-center gap-2 rounded-lg p-2 hover:bg-accent"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {hasChildren && (
          <span className="text-xs text-muted-foreground transition-transform" style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0)" }}>
            ▶
          </span>
        )}
        {!hasChildren && <span className="w-3" />}

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{node.org.name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[node.org.status] ?? "bg-[var(--color-muted)]"}`}>
              {node.org.status}
            </span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{depthLabel}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {node.org.slug} {node.org.industry ? `· ${node.org.industry}` : ""}
            {node.tenants.length > 0 ? ` · ${node.tenants.length} account${node.tenants.length !== 1 ? "s" : ""}` : ""}
          </p>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="mt-1">
          {/* Tenants (sub-accounts) */}
          {node.tenants.map((tenant) => (
            <a
              key={tenant.id}
              href={`/admin/tenants?id=${tenant.id}`}
              className="stagger-item ml-8 flex items-center gap-2 rounded-md p-2 text-sm hover:bg-accent"
            >
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span>{tenant.name}</span>
              <span className={`rounded-full px-1.5 py-0.5 text-xs ${STATUS_COLORS[tenant.status] ?? "bg-[var(--color-muted)]"}`}>
                {tenant.status}
              </span>
            </a>
          ))}

          {/* Child organizations */}
          {node.children.map((child) => (
            <TreeNodeView key={child.org.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function countNodes(node: OrgNode): number {
  return 1 + node.children.reduce((sum, c) => sum + countNodes(c), 0);
}

function countTenants(node: OrgNode): number {
  return node.tenants.length + node.children.reduce((sum, c) => sum + countTenants(c), 0);
}
